import { Controller } from '@hotwired/stimulus';

// フィードバック分析ダッシュボードの月次トレンド折れ線 (#2477)。
// data-feedback-chart-series-value に { labels, metrics:[{key,label,unit,min,max,decimals,color,data}] }
// を JSON で受け取り、指標ボタンで系列を切り替えて inline SVG を描画する。
export default class extends Controller {
  static values = { series: Object };
  static targets = ['svg', 'seg', 'note'];

  connect() {
    this.metrics = this.seriesValue.metrics || [];
    this.labels = this.seriesValue.labels || [];
    this.active = this.metrics.length ? this.metrics[1]?.key || this.metrics[0].key : null;
    this.renderSeg();
    this.render();
  }

  renderSeg() {
    if (!this.hasSegTarget) return;
    this.segTarget.innerHTML = this.metrics
      .map(
        (m) =>
          `<button type="button" data-key="${m.key}" class="${m.key === this.active ? 'on' : ''}">` +
          `<span class="sw" style="background:${m.color}"></span>${m.label}</button>`,
      )
      .join('');
    this.segTarget.querySelectorAll('button').forEach((b) => {
      b.addEventListener('click', () => {
        this.active = b.dataset.key;
        this.renderSeg();
        this.render();
      });
    });
  }

  render() {
    const m = this.metrics.find((x) => x.key === this.active);
    if (!m || !this.hasSvgTarget) return;

    const CW = 760;
    const CH = 250;
    const PL = 44;
    const PR = 20;
    const PT = 22;
    const PB = 34;
    const plotW = CW - PL - PR;
    const plotH = CH - PT - PB;

    const values = m.data.map((v) => (v == null ? 0 : v));
    const min = m.min != null ? m.min : 0;
    const max = m.max != null ? m.max : Math.max(1, ...values) * 1.1;
    const x = (i) => PL + (plotW * i) / Math.max(1, this.labels.length - 1);
    const y = (v) => PT + plotH * (1 - (v - min) / (max - min || 1));
    const fmt = (v) => (m.decimals ? Number(v).toFixed(m.decimals) : Math.round(v));

    let g = '';
    for (let t = 0; t <= 4; t++) {
      const val = min + ((max - min) * t) / 4;
      const yy = y(val);
      g += `<line x1="${PL}" y1="${yy}" x2="${CW - PR}" y2="${yy}" stroke="#eef1f3" stroke-width="1"/>`;
      g += `<text x="${PL - 8}" y="${yy + 3.5}" text-anchor="end" font-size="10" fill="#9aa6ae">${fmt(val)}</text>`;
    }
    this.labels.forEach((lb, i) => {
      g += `<text x="${x(i)}" y="${CH - 12}" text-anchor="middle" font-size="10.5" fill="#697680" font-weight="700">${lb}</text>`;
    });

    const pts = values.map((v, i) => [x(i), y(v)]);
    if (pts.length) {
      const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
      const area =
        `M${pts[0][0]} ${CH - PB} ` +
        pts.map((p) => 'L' + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ') +
        ` L${pts[pts.length - 1][0]} ${CH - PB} Z`;
      g += `<path d="${area}" fill="${m.color}" opacity="0.08"/>`;
      g += `<path d="${line}" fill="none" stroke="${m.color}" stroke-width="2.6" stroke-linejoin="round" stroke-linecap="round"/>`;
      pts.forEach((p, i) => {
        const last = i === pts.length - 1;
        g += `<circle cx="${p[0]}" cy="${p[1]}" r="${last ? 5 : 3.4}" fill="#fff" stroke="${m.color}" stroke-width="${last ? 3 : 2.2}"/>`;
        if (last && m.data[i] != null) {
          g += `<text x="${p[0]}" y="${p[1] - 13}" text-anchor="middle" font-size="12.5" font-weight="800" fill="${m.color}">${fmt(m.data[i])}${m.unit}</text>`;
        }
      });
    }
    this.svgTarget.innerHTML = g;
    this.renderNote(m);
  }

  renderNote(m) {
    if (!this.hasNoteTarget) return;
    const d = m.data.filter((v) => v != null);
    if (d.length < 2) {
      this.noteTarget.innerHTML = `<b>${m.label}</b>のデータが不足しています。`;
      return;
    }
    const cur = d[d.length - 1];
    const prev = d[d.length - 2];
    const first = d[0];
    const fmt = (v) => (m.decimals ? Number(v).toFixed(m.decimals) : Math.round(v));
    const mo = cur - prev;
    const tot = cur - first;
    const moS = (mo >= 0 ? '+' : '−') + fmt(Math.abs(mo));
    const totS = (tot >= 0 ? '+' : '−') + fmt(Math.abs(tot));
    const word = mo > 0 ? '上昇' : mo < 0 ? '低下' : '横ばい';
    this.noteTarget.innerHTML = `<b>${m.label}</b>は前月比 <b>${moS}${m.unit}</b>で${word}。期間で <b>${totS}${m.unit}</b>（${fmt(first)}${m.unit}→${fmt(cur)}${m.unit}）。`;
  }
}
